/**
 * useAuditLog — First service hook. Every other hook uses this.
 * (v4.8 Spec — Phase 1 Mandate, item 2)
 *
 * Every create/update/delete in the platform writes to audit_logs via this hook.
 * Components never call this directly — other service hooks import it.
 *
 * Immutable: audit_logs cannot be updated or deleted (enforced at DB level).
 */

import { useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { createServiceError, fromSupabaseError } from '@/lib/errors';
import type {
  ServiceResult,
  AuditAction,
  AuditEntityType,
  UUID,
} from '@/types';
import type { AuditLog } from '@/types/database';

/** Parameters for logging an audit entry */
interface AuditLogParams {
  readonly organisationId: UUID;
  readonly action: AuditAction;
  readonly entityType: AuditEntityType;
  readonly entityId: UUID;
  readonly beforeValue?: Record<string, unknown> | null;
  readonly afterValue?: Record<string, unknown> | null;
  readonly metadata?: Record<string, unknown> | null;
}

/** Query parameters for reading audit logs */
interface AuditLogQuery {
  readonly organisationId: UUID;
  readonly entityType?: AuditEntityType;
  readonly entityId?: UUID;
  readonly userId?: UUID;
  readonly limit?: number;
  readonly offset?: number;
}

/** Return type for useAuditLog hook */
interface UseAuditLogReturn {
  /** Write an immutable audit entry. Called by every other service hook. */
  readonly log: (params: AuditLogParams) => Promise<ServiceResult<AuditLog>>;
  /** Query audit logs with filters. Directors and managers only (RLS enforced). */
  readonly query: (params: AuditLogQuery) => Promise<ServiceResult<AuditLog[]>>;
}

export function useAuditLog(): UseAuditLogReturn {
  /**
   * Write an immutable audit entry.
   * Fire-and-forget from the caller's perspective — but errors are surfaced.
   */
  const log = useCallback(async (params: AuditLogParams): Promise<ServiceResult<AuditLog>> => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        return {
          data: null,
          error: createServiceError('PERMISSION_DENIED', 'User not authenticated', {
            severity: 'critical',
          }),
          loading: false,
        };
      }

      const { data, error } = await supabase
        .from('audit_logs')
        .insert({
          organisation_id: params.organisationId,
          user_id: user.id,
          action: params.action,
          entity_type: params.entityType,
          entity_id: params.entityId,
          before_value: params.beforeValue ?? null,
          after_value: params.afterValue ?? null,
          metadata: params.metadata ?? null,
        })
        .select()
        .single();

      if (error) {
        // Audit log failure is CRITICAL — v4.8 spec requires Director notification
        return {
          data: null,
          error: fromSupabaseError(error),
          loading: false,
        };
      }

      return { data: data as AuditLog, error: null, loading: false };
    } catch (err) {
      return {
        data: null,
        error: createServiceError(
          'UNKNOWN_ERROR',
          err instanceof Error ? err.message : 'Failed to write audit log',
          { severity: 'critical', retry: true }
        ),
        loading: false,
      };
    }
  }, []);

  /**
   * Query audit logs with filtering.
   * RLS ensures only directors and managers can read.
   */
  const query = useCallback(async (params: AuditLogQuery): Promise<ServiceResult<AuditLog[]>> => {
    try {
      let queryBuilder = supabase
        .from('audit_logs')
        .select('*')
        .eq('organisation_id', params.organisationId)
        .order('created_at', { ascending: false });

      if (params.entityType) {
        queryBuilder = queryBuilder.eq('entity_type', params.entityType);
      }
      if (params.entityId) {
        queryBuilder = queryBuilder.eq('entity_id', params.entityId);
      }
      if (params.userId) {
        queryBuilder = queryBuilder.eq('user_id', params.userId);
      }

      queryBuilder = queryBuilder
        .range(
          params.offset ?? 0,
          (params.offset ?? 0) + (params.limit ?? 50) - 1
        );

      const { data, error } = await queryBuilder;

      if (error) {
        return {
          data: null,
          error: fromSupabaseError(error),
          loading: false,
        };
      }

      return { data: (data ?? []) as AuditLog[], error: null, loading: false };
    } catch (err) {
      return {
        data: null,
        error: createServiceError(
          'UNKNOWN_ERROR',
          err instanceof Error ? err.message : 'Failed to query audit logs',
          { retry: true }
        ),
        loading: false,
      };
    }
  }, []);

  return { log, query };
}
